import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import { isPresent, VERSION } from '@mbscan/shared'
import LogRocket from 'logrocket'
import posthog from 'posthog-js'

import { env } from './env'

export type MonitoringUser = Readonly<{
  email: string
  id: string
  name: string
  role: string
  tenantId: string
  tenantName: string
}>

const posthogEnabled = isPresent(env.posthogApiKey) && isPresent(env.posthogApiUrl)
const fallbackConsole = globalThis.console

export function initializeMonitoring(): void {
  if (isPresent(env.bugsnagApiKey)) {
    Bugsnag.start({
      apiKey: env.bugsnagApiKey,
      appVersion: VERSION,
      plugins: [new BugsnagPluginReact()],
      releaseStage: env.mode,
    })
  }
  if (isPresent(env.posthogApiKey) && isPresent(env.posthogApiUrl)) {
    posthog.init(env.posthogApiKey, {
      api_host: env.posthogApiUrl,
      // warn/error をセッションリプレイと横断して調査できるよう、外部ログ基盤を PostHog Logs に統一する。
      logs: { serviceName: 'mbscan-client', environment: env.mode },
    })
  }
  if (env.mode === 'production') {
    LogRocket.init('czedne/mbscan')
  }
}

export function identifyMonitoringUser(user: MonitoringUser): void {
  if (isPresent(env.bugsnagApiKey)) {
    Bugsnag.setUser(user.id, user.email, user.name)
  }
  if (posthogEnabled) {
    posthog.identify(user.id, user)
  }
  if (env.mode === 'production') {
    LogRocket.identify(user.id, user)
  }
}

export function trackPage(pathname: string, user: MonitoringUser): void {
  // アクセス計測には集計に必要な識別子だけを送り、氏名・メールアドレス・現場名は重複送信しない。
  const properties = { pathname, role: user.role, tenantId: user.tenantId, userId: user.id }
  if (posthogEnabled && user.role !== 'admin') {
    posthog.capture('visit', properties)
  }
}

export type ReportedErrorSeverity = 'error' | 'warning' | 'info'

/**
 * severity は BugSnag のトリアージ用。通信断・古いバンドル・業務エラーのように、こちらのコードを
 * 直しても解消しないものまで同じ重さで通知すると、本当に見るべきエラーが埋もれる。
 * 呼び出し側で分類できないもの（画面のクラッシュ等）は既定の error のままでよい。
 */
export function reportError(cause: unknown, severity: ReportedErrorSeverity = 'error'): void {
  const error = cause instanceof Error ? cause : new Error('予期しないエラーが発生しました。')
  if (isPresent(env.bugsnagApiKey)) {
    Bugsnag.notify(error, (event) => {
      event.severity = severity
    })
  }
  if (posthogEnabled) {
    posthog.captureException(error)
    if (severity === 'error') {
      posthog.logger.error(error.message, { name: error.name })
    } else if (severity === 'warning') {
      posthog.logger.warn(error.message, { name: error.name })
    }
  } else if (severity === 'error') {
    fallbackConsole.error(error.message, { name: error.name })
  } else if (severity === 'warning') {
    fallbackConsole.warn(error.message, { name: error.name })
  }
}
